// Per-space action handlers + memoized SpaceActions object, mirroring
// the `useChannelActions` / `useDmActions` pattern.
//
// `spaceActionsCtxRef` is load-bearing: SpaceRow is wrapped in React.memo,
// and Sidebar re-renders frequently (Zero status flips). If the
// `actions` prop identity changed each render, every SpaceRow would
// re-render. Funnel the live state through a ref so the actions object
// stays referentially stable forever — SpaceRow.memo bails out cleanly.

import { useMemo, useRef } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySpace = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WorkspaceLike = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InviteContext = any;
type CreateChannelContext =
  | { spaceId: string; spaceName: string; hostWorkspaceId?: string }
  | "workspace"
  | null;

export interface SpaceActions {
  /** Toggle the expanded/collapsed state of a space's channel list. */
  toggleExpanded: (spaceId: string) => void;
  /** Open the "create channel in this space" flow. */
  openCreateChannel: (space: AnySpace) => void;
  /** Open the "invite to space" flow. Requires workspace context. */
  openInvite: (space: AnySpace) => void;
  /** Open the space settings panel. */
  openSettings: (spaceId: string) => void;
}

interface UseSpaceActionsArgs {
  toggleSpaceExpanded: (spaceId: string) => void;
  setCreateChannelContext: (ctx: CreateChannelContext) => void;
  setInviteContext: (ctx: InviteContext) => void;
  setSelectedClientSpace: (spaceId: string | null) => void;
  selectedWorkspace: string | null;
  currentWorkspace: WorkspaceLike;
}

export function useSpaceActions({
  toggleSpaceExpanded,
  setCreateChannelContext,
  setInviteContext,
  setSelectedClientSpace,
  selectedWorkspace,
  currentWorkspace,
}: UseSpaceActionsArgs): SpaceActions {
  const ctxRef = useRef({
    toggleSpaceExpanded,
    setCreateChannelContext,
    setInviteContext,
    setSelectedClientSpace,
    selectedWorkspace,
    currentWorkspace,
  });
  // eslint-disable-next-line react-hooks/refs
  ctxRef.current = {
    toggleSpaceExpanded,
    setCreateChannelContext,
    setInviteContext,
    setSelectedClientSpace,
    selectedWorkspace,
    currentWorkspace,
  };

  return useMemo<SpaceActions>(
    () => ({
      toggleExpanded: (spaceId) => {
        ctxRef.current.toggleSpaceExpanded(spaceId);
      },
      openCreateChannel: (space) => {
        ctxRef.current.setCreateChannelContext({
          spaceId: space.id,
          spaceName: space.name,
          hostWorkspaceId: space.workspace_id,
        });
      },
      openInvite: (space) => {
        const ctx = ctxRef.current;
        if (!ctx.selectedWorkspace) return;
        ctx.setInviteContext({
          type: "client_space",
          workspaceId: ctx.selectedWorkspace,
          spaceId: space.id,
          spaceName: space.name,
          workspaceName: ctx.currentWorkspace?.name || "",
          workspaceLogoUrl: ctx.currentWorkspace?.logo_url,
        });
      },
      openSettings: (spaceId) => {
        ctxRef.current.setSelectedClientSpace(spaceId);
      },
    }),
    [],
  );
}
